import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAgreementContext } from "../../context";
import {
  useAgreementData,
  useAgreementFileDownloads,
  useAgreementFileUrl,
  toFileDownloadsKey,
} from "../../hooks";
import {
  AgreementAttachmentsDisplay,
  AgreementFileDownloadsInfo,
} from "../shared";
import { canReadAgreementDocuments } from "../../permissions/agreementDocumentsAccess";
import { AGREEMENT_DOCUMENTS_ATTACHMENT_TYPE, Module } from "@/constants";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import type { AttachmentFileItem } from "@/components/AttachmentsDisplaySection";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useAccountRoleCheck } from "@/modules/account/hooks/useAccountRoleCheck";

/**
 * Sekcja „Dokumenty" (oświadczenia, umowy, aneksy). Od ASMMNT-83 etap 2 pobranie i podgląd
 * idą przez `POST …/files/download-url` (zdarzenie), a pod każdym plikiem jest linia pobrań.
 */
export const AgreementDocumentsSection = () => {
  const { t } = useTranslation();
  const { agreementId } = useAgreementContext();
  const { agreement_files, non_draft, file_downloads, file_download_totals } =
    useAgreementData();
  const { agreements_documents_read } = useModulePermissions(Module.Agreements);
  const { role } = useAccountRoleCheck();
  const { resolveFileUrl } = useAgreementFileUrl(agreementId);
  const downloadsByPath = useAgreementFileDownloads(
    file_downloads,
    file_download_totals,
  );

  const renderFileMeta = useCallback(
    (file: AttachmentFileItem) => (
      <AgreementFileDownloadsInfo
        filename={file.filename}
        summary={
          file.path
            ? downloadsByPath.get(toFileDownloadsKey(file.path))
            : undefined
        }
      />
    ),
    [downloadsByPath],
  );

  if (!agreement_files || agreement_files.length === 0) {
    return null;
  }

  const canReadDocuments = canReadAgreementDocuments({
    hasDocumentsReadPermission: agreements_documents_read,
    roleCode: role,
    citizenshipName: non_draft.citizenship_name,
  });

  const attachmentGroups = [
    {
      key: "agreement_files",
      files: agreement_files,
      title: t("agreements.attachments.agreement_files"),
    },
  ];

  return (
    <CollapsibleSection title={t("agreements.documents.title")}>
      <AgreementAttachmentsDisplay
        attachments={attachmentGroups}
        attachmentType={AGREEMENT_DOCUMENTS_ATTACHMENT_TYPE}
        canRead={canReadDocuments}
        resolveFileUrl={resolveFileUrl}
        renderFileMeta={renderFileMeta}
      />
    </CollapsibleSection>
  );
};
